/**
 * Milestone view-model: types + the pure `buildMilestoneView` derivation (§6.4
 * FE-4). Mirrors the prototype's `renderVals()` milestones block but is
 * React-free and unit-testable. The thresholds it runs over live separately in
 * `milestones.config.ts`.
 */
import { MILESTONE_THRESHOLDS } from "./milestones.config";

export type MilestoneState = "done" | "next" | "future";

export interface MilestoneNode {
  /** The target user count for this milestone. */
  value: number;
  state: MilestoneState;
  /** Whether a connector segment is drawn leading into this node (all but the first). */
  showConnector: boolean;
  /** Fill % (0–100) of the connector leading into this node. */
  connectorPct: number;
  /** The first node uses the "Erste X" label; the rest use "X User". */
  first: boolean;
}

export interface MilestoneView {
  nodes: MilestoneNode[];
  /** How many milestones are already reached. */
  doneCount: number;
  /** Total number of milestones. */
  total: number;
  /** True once every milestone is reached. */
  allDone: boolean;
  /** Current user count (drives all the math). */
  currentUsers: number;
  /** The next milestone's target (or the last one, if all are done). */
  nextValue: number;
  /** Users still missing until the next milestone (0 if all done). */
  nextRemaining: number;
  /** The previously-hit milestone value — the progress bar's lower bound. */
  prevValue: number;
  /** Fill % (0–100) of the next-milestone progress bar. */
  nextPct: number;
}

function pct(current: number, from: number, to: number): number {
  if (current >= to) return 100;
  if (current <= from) return 0;
  return Math.round(((current - from) / (to - from)) * 100);
}

/**
 * Derive the milestone view-model from the current user count.
 * `thresholds` is injectable for tests; defaults to {@link MILESTONE_THRESHOLDS}.
 */
export function buildMilestoneView(
  totalUsers: number,
  thresholds: readonly number[] = MILESTONE_THRESHOLDS,
): MilestoneView {
  const nextIdx = thresholds.findIndex((v) => totalUsers < v);
  const allDone = nextIdx < 0;

  const nodes: MilestoneNode[] = thresholds.map((value, i) => {
    const done = totalUsers >= value;
    const isNext = i === nextIdx;
    const prev = i === 0 ? 0 : thresholds[i - 1];
    return {
      value,
      state: done ? "done" : isNext ? "next" : "future",
      showConnector: i > 0,
      connectorPct: pct(totalUsers, prev, value),
      first: i === 0,
    };
  });

  const nextValue = allDone ? thresholds[thresholds.length - 1] : thresholds[nextIdx];
  const prevValue = nextIdx <= 0 ? 0 : thresholds[nextIdx - 1];

  return {
    nodes,
    doneCount: thresholds.filter((v) => totalUsers >= v).length,
    total: thresholds.length,
    allDone,
    currentUsers: totalUsers,
    nextValue,
    nextRemaining: Math.max(0, nextValue - totalUsers),
    prevValue,
    nextPct: allDone ? 100 : pct(totalUsers, prevValue, nextValue),
  };
}
